import React, { useState } from 'react';
import { Search, MapPin, Building2, ChevronDown, Crosshair, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { animalTypes } from '../../data/mock';
import { toast } from 'sonner';

const SearchBar = ({ onSearch, onGeolocate, isLocating }) => {
  const [searchName, setSearchName] = useState('');
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [location, setLocation] = useState('');

  const handleSearch = () => {
    try {
      if (onSearch) {
        onSearch({ 
          searchName: searchName || '', 
          selectedAnimal: selectedAnimal || null, 
          location: location || '' 
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Une erreur est survenue lors de la recherche');
    }
  };

  const handleGeolocate = () => {
    try {
      if (onGeolocate) {
        onGeolocate();
      }
    } catch (error) {
      console.error('Geolocation error:', error);
      toast.error('Impossible de récupérer votre position');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl md:rounded-full shadow-lg p-3 md:p-2 flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-2 max-w-4xl mx-auto">
      {/* Search by name */}
      <div className="flex items-center gap-3 px-4 py-3 md:py-2 flex-1 min-w-0 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700">
        <Building2 className="w-5 h-5 text-green-500 flex-shrink-0" />
        <div className="flex-1">
          <label className="block text-xs text-gray-400 dark:text-gray-500 mb-1 md:hidden">
            Nom de la clinique
          </label>
          <Input
            type="text"
            placeholder="Nom de la clinique..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyPress={handleKeyPress}
            className="border-0 shadow-none focus-visible:ring-0 px-0 text-sm dark:bg-transparent dark:text-white"
            data-testid="search-name-input"
          />
        </div>
      </div>

      {/* Animal type dropdown */}
      <div className="flex items-center gap-3 px-4 py-3 md:py-2 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700">
        <div className="flex-1">
          <label className="block text-xs text-gray-400 dark:text-gray-500 mb-1 md:hidden">
            Type d'animal
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger 
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors outline-none w-full md:w-auto"
              data-testid="animal-dropdown"
            >
              <span className="text-xl">{selectedAnimal?.icon || '🐾'}</span>
              <span className="flex-1 text-left">{selectedAnimal?.label || 'Tous les animaux'}</span>
              <ChevronDown className="w-4 h-4 text-green-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem
                onClick={() => setSelectedAnimal(null)}
                className="cursor-pointer"
              >
                <span className="mr-2">🐾</span>
                Tous les animaux
              </DropdownMenuItem>
              {animalTypes && animalTypes.map((animal) => (
                <DropdownMenuItem
                  key={animal.id}
                  onClick={() => setSelectedAnimal(animal)}
                  className="cursor-pointer"
                >
                  <span className="mr-2">{animal.icon}</span>
                  {animal.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Location with Geolocate button */}
      <div className="flex items-center gap-3 px-4 py-3 md:py-2 flex-1 min-w-0">
        <MapPin className="w-5 h-5 text-green-500 flex-shrink-0" />
        <div className="flex-1">
          <label className="block text-xs text-gray-400 dark:text-gray-500 mb-1 md:hidden">
            Ville ou code postal
          </label>
          <Input
            type="text"
            placeholder="Ville ou code postal..."
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onKeyPress={handleKeyPress}
            className="border-0 shadow-none focus-visible:ring-0 px-0 text-sm dark:bg-transparent dark:text-white"
            data-testid="search-location-input"
          />
        </div>
        <button
          onClick={handleGeolocate}
          disabled={isLocating}
          className="p-2 rounded-full hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors text-green-600 disabled:opacity-50"
          title="Me localiser automatiquement"
          data-testid="geolocate-button"
        >
          {isLocating ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Crosshair className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Search Button */}
      <Button
        onClick={handleSearch}
        className="bg-orange-400 hover:bg-orange-500 text-white rounded-full px-8 py-3 md:py-2 flex items-center justify-center gap-2 transition-all duration-200 font-medium shadow-lg shadow-orange-400/30 hover:shadow-orange-500/40"
        data-testid="search-submit-button"
      >
        <Search className="w-5 h-5" />
        <span>Rechercher</span>
      </Button>
    </div>
  );
};

export default SearchBar;
